/**
 * Submits a manual integration request (e.g. delivery note print, CREATEORDER resend) and waits for the connectors'
 * verdicts, so the user only sees success once the connector has actually handled the message.
 */

/** The connector verdict fields read back for each status token. */
export type IntegrationMessageStatus = {
   resultTs?: unknown
   messageFailed?: boolean | null
   messageIgnored?: boolean | null
};

/** User-facing errors for each way a submitted integration request can end without success. */
export type IntegrationRequestErrors = {
   notSubmitted: string
   failed: string
   ignored: string
   timedOut: string
};

// A lost response must not automatically submit the request again.
export const integrationRequestContext = (signal: AbortSignal) => ({
   skipRetry: true,
   fetchOptions: {signal}
});

/** Submits once, then polls the status tokens until every message has a result; rejects on failure, ignore or after 60s. */
export const submitIntegrationRequest = async (
   request: (signal: AbortSignal) => Promise<string[] | null | undefined>,
   readStatus: (tokens: string[], signal: AbortSignal) => Promise<(IntegrationMessageStatus | null)[] | null | undefined>,
   errors: IntegrationRequestErrors
): Promise<void> => {
   const controller = new AbortController();
   let timeout: ReturnType<typeof setTimeout> | undefined;
   let pollTimer: ReturnType<typeof setTimeout> | undefined;
   const deadline = new Promise<never>((_, reject) => {
      timeout = setTimeout(() => {
         reject(new Error(errors.timedOut));
         controller.abort();
      }, 60000);
   });
   const submitAndWait = async () => {
      const tokens = await request(controller.signal);
      if (!tokens?.length) {
         throw new Error(errors.notSubmitted);
      }
      while (!controller.signal.aborted) {
         const messages = await readStatus(tokens, controller.signal);
         if (messages?.some(message => message?.messageFailed)) {
            throw new Error(errors.failed);
         }
         if (messages?.some(message => message?.messageIgnored)) {
            throw new Error(errors.ignored);
         }
         if (messages?.length === tokens.length && messages.every(message => message?.resultTs)) {
            return;
         }
         if (controller.signal.aborted) return;
         await new Promise<void>(resolve => {pollTimer = setTimeout(() => resolve(), 1000)});
      }
   };
   try {
      await Promise.race([submitAndWait(), deadline]);
   } finally {
      clearTimeout(timeout);
      clearTimeout(pollTimer);
      controller.abort();
   }
};
